import React, {createElement, forwardRef} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {FlatButtonIcon} from './flat-button-icon';

import styles from './flat-button.component.module.styl';

import type {ComponentProps, ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);
const noop = () => undefined;

type NativeButtonProps = Omit<ComponentProps<'button'>, 'ref'>;

export interface FlatButtonProps extends NativeButtonProps {
    /**
     * Root element type. Use `"a"` together with `href` to render a link styled
     * as a flat button. Defaults to `"button"`.
     */
    as?: 'button' | 'a',
    /**
     * When `true`, the button is disabled and an `aria-label="Loading"` is applied.
     * Click events are suppressed. Use to indicate an in-progress action.
     */
    busy?: boolean,
    /**
     * Applies legacy dark-mode overrides.
     *
     * @deprecated It will be removed in 6 months 03-03-2026. Use default appearance
     */
    darkMode?: boolean,
    /**
     * URL the element navigates to when `as="a"`. Has no effect when rendering
     * as a `<button>`. Temporary prop until the legacy Flow layer is removed.
     */
    href?: string,
    /**
     * Forwarded ref to the underlying `<button>` or `<a>` element.
     */
    ref?: React.Ref<HTMLButtonElement>,
    /**
     * Icon rendered to the left of the button label. Accepts any React node —
     * typically an `<Icon>` component.
     */
    startIcon?: ReactNode,
    /**
     * Link target attribute when `as="a"` (e.g. `"_blank"`). Temporary prop
     * until the legacy Flow layer is removed.
     */
    target?: ComponentProps<'a'>['target'],
    /**
     * Icon rendered to the right of the button label. Accepts any React node —
     * typically an `<Icon>` component.
     */
    endIcon?: ReactNode,
    /**
     * Visual style preset:
     * - `"primary"` — brand-colored text button (default).
     * - `"secondary"` — muted secondary text button.
     *
     * For bold label text, wrap `children` in a `<strong>` tag.
     */
    variant?: 'primary' | 'secondary',
}

/**
 * Inline text button with primary/secondary variants and a busy loading state.
 *
 * Renders as a `<button>` by default; pass `as="a"` with `href` to produce a
 * link. Use `startIcon`/`endIcon` to add icons flanking the label, and wrap
 * `children` in `<strong>` when bold label text is needed.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-flat-button--documentation}
 */

export const FlatButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, FlatButtonProps>(
    (props, ref) => {
        const {
            as = 'button',
            type = as === 'button' ? 'button' : undefined,
            busy,
            disabled,
            children,
            className,
            darkMode,
            endIcon,
            startIcon,
            variant = 'primary',
            ...restProps
        } = props;

        const iconProps = {flat: true, busy, darkMode};

        const content = (
            <div className={classes['flat-button__content']}>
                <FlatButtonIcon {...iconProps} iconClassName={classes['flat-button__icon']}>
                    {startIcon}
                </FlatButtonIcon>
                <span className={classes['flat-button__caption']}>{children}</span>
                <FlatButtonIcon
                    {...iconProps}
                    iconClassName={classNames('flat-button__icon', 'flat-button__icon_right', 'pdx-xxs')}
                    right={true}>
                    {endIcon}
                </FlatButtonIcon>
            </div>
        );

        const elementClassName = classNames(
            'flat-button',
            className,
            {
                'flat-button_dark-mode': darkMode,
                'flat-button_secondary': variant === 'secondary',
                'flat-button_busy': busy,
            },
        );

        const isDisabled = disabled || busy;

        return createElement(as, {
            'aria-label': busy ? 'Loading' : undefined,
            type,
            ...restProps,
            'aria-disabled': isDisabled,
            'disabled': isDisabled,
            'onClick': isDisabled ? noop : restProps.onClick,
            'className': elementClassName,
            ref,
        }, content);
    },
);

FlatButton.displayName = 'FlatButton';
